
import React, { useState, useEffect, useMemo } from 'react';
import { X, ShieldCheck, Loader2, Wand2, Check, AlertCircle, Info, Mail, ArrowLeft, Key, Zap, ShieldAlert } from 'lucide-react';
import { supabase } from '../lib/supabase.ts';
import { trackEvent } from '../lib/analytics.ts';
import { AuthView } from '../types.ts';

interface AuthPortalProps {
  authView: AuthView;
  setAuthView: (view: AuthView) => void;
  notify: (msg: string, type?: any) => void;
  isUserLoggedIn?: boolean;
}

const BRAINROT_PREFIXES = ['Sigma', 'Skibidi', 'Alpha', 'Rizz', 'Fanum', 'Ohio', 'Giga', 'Mewing', 'Glazed', 'Based', 'Aura', 'Sus', 'Mogger', 'Glitch', 'Phantom', 'Gyatt'];
const BRAINROT_SUFFIXES = ['Enjoyer', 'King', 'Phantom', 'Cooker', 'Mogger', 'Taxer', 'Warrior', 'Entity', 'Glitch', 'Titan', 'Ghost', 'Prophet', 'Stalker', 'Ranger'];

export default function AuthPortal({ authView, setAuthView, notify, isUserLoggedIn }: AuthPortalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'valid' | 'taken' | 'short'>('idle');
  const [isVerificationPending, setIsVerificationPending] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Reset local state when authView changes
  useEffect(() => {
    setIsVerificationPending(false);
    setLocalError(null);
  }, [authView]);

  // Generate a random brainrot username
  const generateRizzName = () => {
    const p = BRAINROT_PREFIXES[Math.floor(Math.random() * BRAINROT_PREFIXES.length)];
    const s = BRAINROT_SUFFIXES[Math.floor(Math.random() * BRAINROT_SUFFIXES.length)];
    const num = Math.floor(Math.random() * 9999);
    const newName = `${p}${s}${num}`;
    setUsername(newName);
    validateUsername(newName);
    setLocalError(null);
  };

  // Close automatically if we detect a session (unless resetting password)
  useEffect(() => {
    if (isUserLoggedIn && authView !== 'reset_password') {
      setAuthView('app');
    }
  }, [isUserLoggedIn, authView, setAuthView]);

  const validateUsername = async (val: string) => {
    if (val.length < 3) {
      setUsernameStatus('short');
      return;
    }
    setIsCheckingUsername(true);
    try {
      const { data } = await supabase
        .from('profiles')
        .select('username')
        .ilike('username', val)
        .maybeSingle();
      setUsernameStatus(data ? 'taken' : 'valid');
    } catch {
      setUsernameStatus('idle');
    } finally {
      setIsCheckingUsername(false);
    }
  };

  // Debounce username check
  useEffect(() => {
    if (authView === 'signup' && username) {
      const timer = setTimeout(() => validateUsername(username), 500);
      return () => clearTimeout(timer);
    } else {
      setUsernameStatus('idle');
    }
  }, [username, authView]);

  const emailIsValid = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email), [email]);
  const passwordIsStrong = useMemo(() => password.length >= 8, [password]);

  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (authLoading || !emailIsValid) return;
    
    setAuthLoading(true);
    setLocalError(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin
      });
      if (error) throw error;
      notify("RECOVERY FREQUENCY SENT!", "success");
      setIsVerificationPending(true);
    } catch (err: any) {
      setLocalError(err.message.toUpperCase());
      notify(err.message.toUpperCase(), "error");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (authLoading || !passwordIsStrong) return;

    setAuthLoading(true);
    setLocalError(null);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      notify("SECURITY OVERRIDE GRANTED!", "success");
      setAuthView('app');
    } catch (err: any) {
      setLocalError(err.message.toUpperCase());
      notify(err.message.toUpperCase(), "error");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (authLoading) return;

    setLocalError(null);

    if (!emailIsValid) {
      const msg = "INVALID EMAIL FORMAT";
      setLocalError(msg);
      return notify(msg, "error");
    }

    if (!passwordIsStrong) {
      const msg = "PASSWORD MUST BE AT LEAST 8 CHARS";
      setLocalError(msg);
      return notify(msg, "error");
    }

    if (authView === 'signup') {
      if (usernameStatus === 'taken') {
        const msg = "USERNAME ALREADY TAKEN";
        setLocalError(msg);
        return notify(msg, "error");
      }
      if (usernameStatus === 'short') {
        const msg = "USERNAME TOO SHORT";
        setLocalError(msg);
        return notify(msg, "error");
      }
      if (usernameStatus === 'idle') return notify("PLEASE WAIT FOR USERNAME CHECK", "info");
    }
    
    setAuthLoading(true);
    try {
      if (authView === 'login') {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          if (error.message.includes('Invalid login credentials')) {
            throw new Error("WRONG EMAIL OR PASSWORD. TRY AGAIN, SIGMA.");
          }
          throw error;
        }
        if (data.session) {
          trackEvent('login', { method: 'email' });
          setAuthView('app');
          notify("WELCOME BACK, SIGMA", "success");
        }
      } else {
        const { data, error } = await supabase.auth.signUp({ 
          email, 
          password,
          options: {
            data: { username }
          }
        });
        if (error) throw error;
        
        if (data.user && data.session) {
           trackEvent('sign_up', { method: 'email' });
           setAuthView('app');
           notify("PROFILE FORGED!", "success");
        } else {
           setIsVerificationPending(true);
           notify("VERIFICATION TRANSMISSION SENT!", "info");
        }
      }
    } catch (err: any) {
      setLocalError(err.message.toUpperCase());
      notify(err.message.toUpperCase(), "error");
    } finally {
      setAuthLoading(false);
    }
  };

  if (isUserLoggedIn && authView !== 'reset_password') return null;

  if (isVerificationPending) {
    return (
      <div className="fixed inset-0 z-[1000] bg-black/95 flex items-center justify-center p-4 backdrop-blur-md overflow-y-auto">
        <div className="w-full max-w-md bg-zinc-900 border-4 border-blue-500 rounded-[3.5rem] p-8 md:p-12 shadow-[0_0_100px_rgba(59,130,246,0.2)] space-y-8 relative animate-in zoom-in-95 my-auto text-center">
          <div className="mx-auto w-20 h-20 bg-blue-500 rounded-3xl flex items-center justify-center text-black shadow-lg mb-4 animate-bounce">
            <Mail size={40}/>
          </div>
          <div className="space-y-4">
            <h2 className="text-4xl font-bungee text-blue-400 uppercase leading-tight">CHECK YOUR INBOX</h2>
            <p className="text-sm font-bold text-zinc-300">
              {authView === 'forgot' 
                ? "We've sent a recovery link to forge a new frequency." 
                : "We've sent a secret frequency code to confirm your DNA."
              }
            </p>
            <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest leading-relaxed bg-zinc-950 p-4 rounded-2xl border border-zinc-800">
              IF YOU DON'T SEE IT IN 60 SECONDS, CHECK YOUR SPAM FOLDER! THE SIGNAL MIGHT BE DISTORTED.
            </p>
          </div>
          
          <button 
            onClick={() => { setIsVerificationPending(false); setAuthView('login'); }}
            className="w-full py-6 bg-zinc-800 text-white font-bungee text-xl rounded-2xl border-2 border-zinc-700 hover:bg-zinc-700 transition-all flex items-center justify-center gap-3"
          >
            <ArrowLeft size={20} /> BACK TO LOGIN
          </button>
        </div>
      </div>
    );
  }

  // PASSWORD RECOVERY / RESET VIEW
  if (authView === 'reset_password') {
    return (
      <div className="fixed inset-0 z-[1000] bg-black/95 flex items-center justify-center p-4 backdrop-blur-md overflow-y-auto">
        <div className="w-full max-w-md bg-zinc-950 border-4 border-indigo-600 rounded-[3.5rem] p-8 md:p-12 shadow-[0_0_100px_rgba(79,70,229,0.3)] space-y-8 relative animate-in zoom-in-95 my-auto">
          <div className="text-center space-y-2">
            <div className="mx-auto w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg mb-4 animate-pulse">
              <Key size={40}/>
            </div>
            <h2 className="text-4xl font-bungee text-indigo-400 uppercase leading-none">SECURITY OVERRIDE</h2>
            <p className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.2em]">Rewrite your DNA Frequency</p>
          </div>
          
          <form onSubmit={handleUpdatePassword} className="space-y-6">
            {localError && (
              <div className="bg-red-600 border-2 border-red-500 p-4 rounded-2xl flex items-center gap-3 animate-in slide-in-from-top-4 animate-shake">
                <ShieldAlert size={20} className="text-white shrink-0" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">{localError}</span>
              </div>
            )}
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest px-1">NEW PASSWORD (8+ CHARS)</label>
                <div className="relative">
                  <input 
                    type="password" 
                    required 
                    value={password} 
                    onChange={e => { setPassword(e.target.value); setLocalError(null); }} 
                    className={`w-full bg-zinc-800 border-2 rounded-2xl p-4 text-white font-bold outline-none transition-all ${password.length > 0 ? (passwordIsStrong ? 'border-emerald-500/50' : 'border-red-500/50') : 'border-zinc-700 focus:border-indigo-500'}`} 
                    placeholder="ENTER NEW PASSWORD" 
                  />
                  {password.length > 0 && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      {passwordIsStrong ? <Check size={16} className="text-emerald-500" /> : <AlertCircle size={16} className="text-red-500" />}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <button type="submit" disabled={authLoading || !passwordIsStrong} className="w-full py-6 bg-indigo-600 text-white font-bungee text-2xl rounded-2xl shadow-[0_8px_0_0_#4338ca] hover:bg-indigo-500 hover:scale-[1.02] active:translate-y-2 active:shadow-none transition-all flex items-center justify-center gap-3 disabled:opacity-50">
              {authLoading ? <Loader2 className="animate-spin" size={24} /> : 'REWRITE DNA'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // FORGOT PASSWORD REQUEST VIEW
  if (authView === 'forgot') {
    return (
      <div className="fixed inset-0 z-[1000] bg-black/95 flex items-center justify-center p-4 backdrop-blur-md overflow-y-auto">
        <div className="w-full max-w-md bg-zinc-900 border-4 border-orange-500 rounded-[3.5rem] p-8 md:p-12 shadow-[0_0_100px_rgba(249,115,22,0.2)] space-y-8 relative animate-in zoom-in-95 my-auto">
          <button onClick={() => setAuthView('login')} className="absolute top-8 right-8 text-zinc-600 hover:text-white transition-colors">
            <X size={32}/>
          </button>
          <div className="text-center space-y-2">
            <div className="mx-auto w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center text-black shadow-lg mb-4">
              <ShieldAlert size={40}/>
            </div>
            <h2 className="text-4xl font-bungee text-orange-500 uppercase leading-none">LOST ACCESS?</h2>
            <p className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.2em]">Broadcast recovery signal</p>
          </div>
          
          <form onSubmit={handleResetRequest} className="space-y-6">
            {localError && (
              <div className="bg-red-600 border-2 border-red-500 p-4 rounded-2xl flex items-center gap-3 animate-in slide-in-from-top-4 animate-shake">
                <ShieldAlert size={20} className="text-white shrink-0" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">{localError}</span>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest px-1">Registered Email</label>
              <div className="relative">
                <input 
                  type="email" 
                  required 
                  value={email} 
                  onChange={e => { setEmail(e.target.value); setLocalError(null); }} 
                  className={`w-full bg-zinc-800 border-2 rounded-2xl p-4 text-white font-bold outline-none transition-all ${email.length > 0 ? (emailIsValid ? 'border-emerald-500/50' : 'border-red-500/50') : 'border-zinc-700 focus:border-orange-500'}`} 
                  placeholder="EMAIL ADDRESS" 
                />
                {email.length > 0 && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    {emailIsValid ? <Check size={16} className="text-emerald-500" /> : <AlertCircle size={16} className="text-red-500" />}
                  </div>
                )}
              </div>
            </div>
            
            <button type="submit" disabled={authLoading || !emailIsValid} className="w-full py-6 bg-orange-500 text-black font-bungee text-xl rounded-2xl shadow-[0_8px_0_0_#9a3412] hover:bg-orange-400 hover:scale-[1.02] active:translate-y-2 active:shadow-none transition-all flex items-center justify-center gap-3 disabled:opacity-50">
              {authLoading ? <Loader2 className="animate-spin" size={24} /> : 'TRANSMIT SIGNAL'}
            </button>
            
            <button 
              type="button" 
              onClick={() => setAuthView('login')} 
              className="w-full text-[10px] text-zinc-500 font-black uppercase tracking-widest hover:text-white transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft size={12} /> Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[1000] bg-black/95 flex items-center justify-center p-4 backdrop-blur-md overflow-y-auto">
      <div className="w-full max-w-md bg-zinc-900 border-4 border-yellow-400 rounded-[3.5rem] p-8 md:p-12 shadow-[0_0_100px_rgba(250,196,21,0.2)] space-y-8 relative animate-in zoom-in-95 my-auto">
        <button onClick={() => setAuthView('app')} className="absolute top-8 right-8 text-zinc-600 hover:text-white transition-colors">
          <X size={32}/>
        </button>
        <div className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 bg-yellow-400 rounded-2xl flex items-center justify-center text-black shadow-lg mb-4">
            <ShieldCheck size={40}/>
          </div>
          <h2 className="text-5xl font-bungee text-yellow-400 uppercase leading-none">
            {authView === 'login' ? 'LOGIN' : 'SIGNUP'}
          </h2>
          <p className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.2em]">Secure your high-aura vault</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {localError && (
            <div className="bg-red-600 border-2 border-red-500 p-4 rounded-2xl flex items-center gap-3 animate-in slide-in-from-top-4 animate-shake">
              <ShieldAlert size={20} className="text-white shrink-0" />
              <span className="text-[10px] font-black text-white uppercase tracking-widest leading-none">{localError}</span>
            </div>
          )}

          {authView === 'signup' && (
            <div className="space-y-2">
              <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest px-1">Choose your handle</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input 
                    type="text" 
                    required 
                    value={username} 
                    onChange={e => { setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, '')); setLocalError(null); }} 
                    className={`w-full bg-zinc-800 border-2 rounded-2xl p-4 pr-10 text-white font-bold outline-none transition-all ${usernameStatus === 'taken' ? 'border-red-500 bg-red-500/5' : usernameStatus === 'valid' ? 'border-emerald-500 bg-emerald-500/5' : 'border-zinc-700 focus:border-yellow-400'}`} 
                    placeholder="USERNAME"
                    maxLength={20}
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    {isCheckingUsername ? <Loader2 size={16} className="animate-spin text-zinc-500" /> : 
                     usernameStatus === 'valid' ? <Check size={16} className="text-emerald-500" /> : 
                     usernameStatus === 'taken' ? <AlertCircle size={16} className="text-red-500" /> : null}
                  </div>
                </div>
                <button 
                  type="button" 
                  onClick={generateRizzName}
                  className="px-5 bg-zinc-800 border-2 border-zinc-700 rounded-2xl text-yellow-400 hover:bg-zinc-700 hover:border-yellow-400 transition-all active:scale-95 group"
                  title="Magic Rizz Name"
                >
                  <Wand2 size={24} className="group-hover:rotate-12 transition-transform" />
                </button>
              </div>
              {usernameStatus === 'taken' && <p className="text-[8px] font-bold text-red-500 uppercase px-2 tracking-widest animate-pulse">Identity Taken by Another Sigma</p>}
              {usernameStatus === 'short' && username.length > 0 && <p className="text-[8px] font-bold text-zinc-500 uppercase px-2 tracking-widest">Name Too Tiny (3+ chars)</p>}
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-zinc-500 uppercase tracking-widest px-1">Account Credentials</label>
              <div className="relative">
                <input 
                  type="email" 
                  required 
                  value={email} 
                  onChange={e => { setEmail(e.target.value); setLocalError(null); }} 
                  className={`w-full bg-zinc-800 border-2 rounded-2xl p-4 text-white font-bold outline-none transition-all ${email.length > 0 ? (emailIsValid ? 'border-emerald-500/50' : 'border-red-500/50') : 'border-zinc-700 focus:border-yellow-400'}`} 
                  placeholder="EMAIL ADDRESS" 
                  autoComplete="email"
                />
                {email.length > 0 && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    {emailIsValid ? <Check size={16} className="text-emerald-500" /> : <AlertCircle size={16} className="text-red-500" />}
                  </div>
                )}
              </div>
              <div className="relative">
                <input 
                  type="password" 
                  required 
                  value={password} 
                  onChange={e => { setPassword(e.target.value); setLocalError(null); }} 
                  className={`w-full bg-zinc-800 border-2 rounded-2xl p-4 text-white font-bold outline-none transition-all ${password.length > 0 ? (passwordIsStrong ? 'border-emerald-500/50' : 'border-red-500/50') : 'border-zinc-700 focus:border-yellow-400'}`} 
                  placeholder="PASSWORD (8+ CHARS)" 
                  autoComplete="current-password"
                />
                {password.length > 0 && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    {passwordIsStrong ? <Check size={16} className="text-emerald-500" /> : <AlertCircle size={16} className="text-red-500" />}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <button type="submit" disabled={authLoading || (authView === 'signup' && usernameStatus !== 'valid')} className="w-full py-6 bg-yellow-400 text-black font-bungee text-2xl rounded-2xl shadow-[0_8px_0_0_#ca8a04] hover:bg-yellow-300 hover:scale-[1.02] active:translate-y-2 active:shadow-none transition-all flex items-center justify-center gap-3 disabled:opacity-50">
            {authLoading ? <Loader2 className="animate-spin" size={24} /> : 'ENTER THE ROT'}
          </button>
          
          <div className="flex flex-col items-center gap-4">
            <div className="flex justify-between w-full px-2">
              <button 
                type="button" 
                onClick={() => setAuthView('forgot')} 
                className="text-[8px] font-black text-zinc-500 uppercase tracking-widest hover:text-orange-500 transition-colors"
              >
                Lost Access?
              </button>
              <button 
                type="button" 
                onClick={() => { setAuthView(authView === 'login' ? 'signup' : 'login'); setUsername(''); setPassword(''); setIsVerificationPending(false); }} 
                className="text-[8px] font-black text-zinc-500 uppercase tracking-widest hover:text-white transition-colors"
              >
                {authView === 'login' ? 'Join the Tribe' : 'Back to Login'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
